import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';

import { PackWithBuyButton } from '../PackWithBuyButton';
import { useItemToDisplay } from '../useItemToDisplay';
import type { DailyDeals_cardPackGroup } from './__generated__/index.graphql';

const PacksWrapper = styled(SBHorizontal)`
  gap: var(--quadruple-unit);
  flex: 1;
  min-width: min-content;
  max-width: 50%;
`;
const ScrollableWrapper = styled.div`
  width: 100%;
`;

type Props = {
  cardPackGroup: DailyDeals_cardPackGroup;
  onClickBuy: (cardPackGroupId: string, cardPackSlug: string) => void;
  forceMobileLayout?: boolean;
};
export const DailyDeals = ({
  cardPackGroup,
  onClickBuy,
  forceMobileLayout = false,
}: Props) => {
  const { ref: scrollableWrapperRef, itemToDisplay } = useItemToDisplay();

  const isLaptopAndAbove = useIsLaptop();
  const { cardPacks } = cardPackGroup;

  if (isLaptopAndAbove && !forceMobileLayout) {
    return (
      <PacksWrapper>
        {cardPacks.map(pack => {
          return (
            <PackWithBuyButton
              key={pack.slug}
              cardPack={pack}
              cardPackGroupId={cardPackGroup.id}
              onClickBuy={onClickBuy}
            />
          );
        })}
      </PacksWrapper>
    );
  }

  return (
    <ScrollableWrapper ref={scrollableWrapperRef}>
      <Scrollable
        itemToDisplay={itemToDisplay}
        overhang={itemToDisplay === 1 ? 0 : 6}
      >
        {cardPacks.map(pack => {
          return (
            <PackWithBuyButton
              key={pack.slug}
              cardPack={pack}
              cardPackGroupId={cardPackGroup?.id || ''}
              onClickBuy={onClickBuy}
            />
          );
        })}
      </Scrollable>
    </ScrollableWrapper>
  );
};

DailyDeals.fragments = {
  cardPackGroup: gql`
    fragment DailyDeals_cardPackGroup on CardPackGroup {
      id
      cardPacks {
        slug
        ...PackWithBuyButton_cardPack
      }
    }
    ${PackWithBuyButton.fragments.cardPack}
  ` as TypedDocumentNode<DailyDeals_cardPackGroup>,
};
